import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '@/i18n';
import { HelpLink, HELP_ARTICLES, PageHeader } from '@vanguard-atlas/shared';
import { Database } from 'lucide-react';
import {
  Button,
  Card,
  Icon,
  SearchInput,
  Skeleton,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from '@/components/ui';
import { toast } from 'sonner';
import { useDataSources, useDeleteDataSource, usePauseDataSource, useResumeDataSource, useTriggerSync } from '../hooks/use-datasources';
import { STATUS_CONFIG, PROVIDER_LABELS } from '../types';
import type { DataSource, DataSourceStatus, ProviderType } from '../types';
import { DatasourceProviderIcon } from '../components/ProviderIcons';

// =============================================================================
// Status Badge
// =============================================================================

function DataSourceStatusBadge({ status }: { status: DataSourceStatus }) {
  const { t } = useI18n();
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.active;

  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-xs font-medium ${config.color}`}>
      {config.animate && <Icon name="loader" size={12} className="animate-spin" />}
      {t(`datasources.status.${status}`)}
    </span>
  );
}

// =============================================================================
// Loading Skeleton
// =============================================================================

function DataSourcesSkeleton() {
  return (
    <div className="space-y-3">
      {[1, 2, 3, 4].map((i) => (
        <Card key={i} className="p-4">
          <div className="flex items-center gap-4">
            <Skeleton className="h-10 w-10 rounded-lg" />
            <div className="flex-1">
              <Skeleton className="h-5 w-1/3 mb-2" />
              <Skeleton className="h-4 w-1/4" />
            </div>
            <Skeleton className="h-6 w-16" />
          </div>
        </Card>
      ))}
    </div>
  );
}

// =============================================================================
// Relative Time Helper
// =============================================================================

function formatRelativeTime(isoDate: string | null, t: (key: string) => string): string {
  if (!isoDate) return t('common.never');
  const diff = Date.now() - new Date(isoDate).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return t('common.timeAgo.justNow');
  if (minutes < 60) return t('common.timeAgo.minutesAgo').replace('{n}', String(minutes));
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t('common.timeAgo.hoursAgo').replace('{n}', String(hours));
  const days = Math.floor(hours / 24);
  return t('common.timeAgo.daysAgo').replace('{n}', String(days));
}

// =============================================================================
// DataSources List Page
// =============================================================================

interface DeleteTarget {
  id: string;
  name: string;
}

export function DataSourcesListPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);

  const { data, isLoading, isError, error, refetch } = useDataSources({
    search: search || undefined,
  });

  const deleteDS = useDeleteDataSource();
  const pauseDS = usePauseDataSource();
  const resumeDS = useResumeDataSource();
  const syncDS = useTriggerSync();

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteDS.mutateAsync(deleteTarget.id);
      toast.success(t('datasources.deleteSuccess').replace('{name}', deleteTarget.name));
      setDeleteTarget(null);
    } catch {
      toast.error(t('datasources.deleteFailed'));
    }
  };

  const handlePause = async (ds: DataSource) => {
    try {
      await pauseDS.mutateAsync(ds.id);
      toast.success(t('datasources.pauseSuccess'));
    } catch {
      toast.error(t('datasources.pauseFailed'));
    }
  };

  const handleResume = async (ds: DataSource) => {
    try {
      await resumeDS.mutateAsync(ds.id);
      toast.success(t('datasources.resumeSuccess'));
    } catch {
      toast.error(t('datasources.resumeFailed'));
    }
  };

  const handleSync = async (ds: DataSource) => {
    try {
      await syncDS.mutateAsync(ds.id);
      toast.success(t('datasources.syncTriggered'));
    } catch {
      toast.error(t('datasources.syncFailed'));
    }
  };

  const handleSearch = (value: string) => {
    setSearch(value);
  };

  const sources = data?.data ?? [];

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <PageHeader
          icon={Database}
          title={t('datasources.title')}
          subtitle={t('datasources.subtitle')}
          testId="datasources-header"
        />
        <div className="flex items-center gap-3">
          <HelpLink
            slug={HELP_ARTICLES.GOOGLE_WORKSPACE}
            label={t('common.help')}
            ariaLabel={`${t('common.help')}: ${t('common.helpOpensNewTab')}`}
            className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
          />
          <Button leftIcon="plus" onClick={() => navigate('/knowledge/datasources/new')}>
            {t('datasources.newDatasource')}
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="mb-6 max-w-md">
        <SearchInput
          placeholder={t('datasources.searchPlaceholder')}
          value={search}
          onChange={(e) => handleSearch(e.target.value)}
          onClear={() => handleSearch('')}
        />
      </div>

      {/* Loading */}
      {isLoading && <DataSourcesSkeleton />}

      {/* Error */}
      {isError && (
        <Card className="p-8 text-center">
          <Icon name="alert-circle" size={48} className="mx-auto text-red-500 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            {t('datasources.loadFailed')}
          </h3>
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            {error instanceof Error ? error.message : t('common.error')}
          </p>
          <Button variant="outline" onClick={() => refetch()}>
            {t('common.retry')}
          </Button>
        </Card>
      )}

      {/* Empty State */}
      {!isLoading && !isError && sources.length === 0 && (
        <Card className="p-8 text-center">
          <Icon name="hard-drive" size={48} className="mx-auto text-gray-300 dark:text-gray-600 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            {t('datasources.noSources')}
          </h3>
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            {search ? t('datasources.noSearchResults') : t('datasources.noSourcesHint')}
          </p>
          {!search && (
            <Button leftIcon="plus" onClick={() => navigate('/knowledge/datasources/new')}>
              {t('datasources.newDatasource')}
            </Button>
          )}
        </Card>
      )}

      {/* DataSources List */}
      {!isLoading && !isError && sources.length > 0 && (
        <div className="space-y-3">
          {sources.map((ds) => (
            <Card
              key={ds.id}
              className="p-4 cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => navigate(`/knowledge/datasources/${ds.id}`)}
            >
              <div className="flex items-center gap-4">
                {/* Provider Icon */}
                <div className="p-2 rounded-lg bg-blue-50 dark:bg-blue-900/30 shrink-0">
                  <DatasourceProviderIcon providerType={ds.provider_type as ProviderType} size={24} />
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-gray-900 dark:text-gray-100 truncate">
                      {ds.name}
                    </h3>
                    <DataSourceStatusBadge status={ds.status as DataSourceStatus} />
                  </div>
                  <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400">
                    <span>{PROVIDER_LABELS[ds.provider_type as ProviderType] ?? ds.provider_type}</span>
                    <span>•</span>
                    <span>
                      {t('datasources.lastSync')}: {formatRelativeTime(ds.last_sync_at, t)}
                    </span>
                    {ds.bound_collection_id && (
                      <>
                        <span>•</span>
                        <span className="flex items-center gap-1">
                          <Icon name="link" size={12} />
                          {t('datasources.bound')}
                        </span>
                      </>
                    )}
                  </div>
                  {ds.error_message && (
                    <p className="text-xs text-red-500 mt-1 truncate">{ds.error_message}</p>
                  )}
                </div>

                {/* Actions */}
                <div onClick={(e) => e.stopPropagation()}>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <Icon name="more-vertical" size={16} />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => navigate(`/knowledge/datasources/${ds.id}`)}>
                        <Icon name="eye" size={14} className="mr-2" />
                        {t('common.view')}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate(`/knowledge/datasources/${ds.id}/edit`)}>
                        <Icon name="edit" size={14} className="mr-2" />
                        {t('common.edit')}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      {ds.status === 'active' || ds.status === 'error' ? (
                        <>
                          <DropdownMenuItem onClick={() => handleSync(ds)}>
                            <Icon name="refresh-cw" size={14} className="mr-2" />
                            {t('datasources.triggerSync')}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handlePause(ds)}>
                            <Icon name="pause" size={14} className="mr-2" />
                            {t('datasources.pause')}
                          </DropdownMenuItem>
                        </>
                      ) : ds.status === 'paused' ? (
                        <DropdownMenuItem onClick={() => handleResume(ds)}>
                          <Icon name="play" size={14} className="mr-2" />
                          {t('datasources.resume')}
                        </DropdownMenuItem>
                      ) : null}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        className="text-red-600 focus:text-red-600"
                        onClick={() => setDeleteTarget({ id: ds.id, name: ds.name })}
                      >
                        <Icon name="trash-2" size={14} className="mr-2" />
                        {t('common.delete')}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('datasources.deleteTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('datasources.deleteDescription').replace('{name}', deleteTarget?.name ?? '')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleteDS.isPending ? t('common.deleting') : t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
